/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @nx/enforce-module-boundaries */
import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Modal } from "@mui/material";
import { useParams, useLocation } from "react-router-dom";
import muteIcon from "packages/novous_web/src/assets/icons/Mute.svg";
import unmuteIcon from "packages/novous_web/src/assets/icons/unMute.svg";
import endCallIcon from "packages/novous_web/src/assets/icons/End Call.svg";
import avatar from "packages/novous_web/src/assets/icons/avatar.svg";
import { API_ENDPOINTS } from "../../api/endpoints";
import useAxios from "../../api/useAxios";
import AlertToast from "./Alert";
import { WebRTCCallService } from "../../service/webRTCCall.service";
import { IAwsCredentials } from "../../types/kinesis";

interface SosModalProps {
  open?: boolean;
  onClose?: () => void;
  tripInfo?: any;
}

const SosModal: React.FC<SosModalProps> = ({
  open = true,
  onClose = () => {},
  tripInfo = { driverName: "Default Driver" },
}) => {
  // ───── Retrieve route parameters and query string ─────────────
  const { vehicleRegNo } = useParams<{ vehicleRegNo: string }>();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const token = queryParams.get("token");

  // ───── Track token validation state ─────────────
  // null: unknown, true: valid, false: invalid.
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);

  // ───── Always call the token validation API hook ─────────────
  const {
    data: validTokenData,
    fetchData: fetchValidTokenData,
  } = useAxios<{ data: any; message: string }>(
    token ? `${API_ENDPOINTS.dashboard.VALID_TOKEN}${token}` : "",
    "GET"
  );

  // ───── Fetch token validation info if token is present ─────────────
  useEffect(() => {
    if (token) {
      fetchValidTokenData();
    } else {
      setIsValidToken(false);
    }
  }, [token]);

  // ───── Process token validation response ─────────────
  useEffect(() => {
    if (validTokenData) {
      console.log("✅ VALID_TOKEN API response:", validTokenData.data);
      if (
        validTokenData.data === null ||
        (Array.isArray(validTokenData.data) && validTokenData.data.length === 0)
      ) {
        setIsValidToken(false);
      } else {
        setIsValidToken(true);
      }
    }
  }, [validTokenData]);

  // ───── Other states and refs ─────────────
  const [toast, setToast] = useState({ open: false, message: "", type: "error" });
  const [connectionState, setConnectionState] = useState<
    "connecting" | "connected" | "disconnected" | "closed"
  >("disconnected");
  const [isMuted, setIsMuted] = useState(false);
  const webrtc = useRef<WebRTCCallService | null>(null);
  const connectionStatus = useRef(-1);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [callDuration, setCallDuration] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const connectingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ───── CALLING API hook ─────────────
  const {
    data: vehicleCallingData,
    fetchData: fetchVehicleCallingData,
  } = useAxios<{ data: any; message: string }>(
    `${API_ENDPOINTS.dashboard.CALLING}${vehicleRegNo}`,
    "GET"
  );

  // ───── STOP_CALLING API hook ─────────────
  const {
    data: vehicleStopCallingData,
    fetchData: fetchVehicleStopCallingData,
  } = useAxios(
    `${API_ENDPOINTS.dashboard.STOP_CALLING}${vehicleRegNo}`,
    "GET"
  );

  const hasInitiatedCall = useRef(false);

  // ───── Trigger the CALLING API only if token is valid ─────────────
  useEffect(() => {
    if (isValidToken && open && vehicleRegNo && !hasInitiatedCall.current) {
      hasInitiatedCall.current = true;
      connectionStatus.current = 0;
      console.log("connecting..");
      setConnectionState("connecting");
      fetchVehicleCallingData();
    }
  }, [isValidToken, vehicleRegNo, open, fetchVehicleCallingData]);

  // ───── Monitor STOP_CALLING API response ─────────────
  useEffect(() => {
    if (isValidToken && vehicleStopCallingData) {
      onClose();
    }
  }, [isValidToken, vehicleStopCallingData, onClose]);

  // ───── Initiate call if calling data is available ─────────────
  useEffect(() => {
    const initiateCall = async () => {
      if (!vehicleCallingData?.data) return;

      if (vehicleCallingData?.message === "Device Is not Online") {
        setToast({ open: true, message: "Device is Offline", type: "error" });
        onClose();
        return;
      }

      const hasMicAccess = await checkMicrophoneAccess();
      if (!hasMicAccess) {
        setToast({ open: true, message: "Microphone is blocked", type: "error" });
        console.log("Microphone blocked");
        handleCallEnd();
        return;
      }

      const awsCredentials: IAwsCredentials = {
        credentials: {
          accessKeyId: vehicleCallingData.data.accessKeyId,
          secretAccessKey: vehicleCallingData.data.secretKeyId,
        },
        region: "ap-south-1",
      };

      console.log(awsCredentials, "aws");
      webrtc.current = new WebRTCCallService(awsCredentials);

      webrtc.current.startCall(
        audioRef,
        handleAudioConnection,
        vehicleCallingData?.data.channelArn,
        handleAudioCallStatus,
        (data: any, status: string, topicKey: string) => {}
      );
    };

    if (isValidToken && vehicleCallingData?.data) {
      initiateCall();
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isValidToken, vehicleCallingData]);

  const handleAudioConnection = (status: boolean) => {
  };

  const handleCallStatus = (status: string) => {
    if (status === "disconnected") setConnectionState("disconnected");
  };

  const handleAudioCallStatus = (status: boolean) => {
    if (status) {
      setConnectionState("connected");
      connectionStatus.current = 1;
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    }
  };

  useEffect(() => {
    console.log(
      connectionState,
      "connectionState",
      open,
      connectionState === "connecting" && open
    );

    if (connectionState === "connecting" && open) {
      console.log("inside connecting phase");
      connectingTimeoutRef.current = setTimeout(() => {
        console.log("setTimeout ended", connectionStatus.current);
        if (connectionStatus.current !== 1) {
          setToast({
            open: true,
            message: "Call could not connect. Please try again.",
            type: "error",
          });
          handleCallEnd();
        }
      }, 60000);
    } else if (connectionState === "connected") {
      if (connectingTimeoutRef.current) {
        clearTimeout(connectingTimeoutRef.current);
        connectingTimeoutRef.current = null;
        console.log(connectionState, "cleared connectingTimeoutRef");
      }
    }

    return () => {
      if (connectingTimeoutRef.current) clearTimeout(connectingTimeoutRef.current);
      connectingTimeoutRef.current = null;
    };
  }, [connectionState, open]);

  const handleMuteToggle = () => {
    const newMuteState = !isMuted;
    if (webrtc.current) {
      webrtc.current.toggleAudioMute((muted) => {
        console.log(muted, "mute");
      });
      setIsMuted(newMuteState);
    }
  };

  const handleCallEnd = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (connectingTimeoutRef.current) clearTimeout(connectingTimeoutRef.current);

    if (webrtc.current) {
      webrtc.current.endCall(
        handleAudioCallStatus,
        (data: any, status: string, topicKey: string) => {}
      );
    }
    setCallDuration(0);
    // Only trigger STOP_CALLING when token is valid
    if (isValidToken) {
      fetchVehicleStopCallingData();
    }
    setConnectionState("closed");
    connectionStatus.current = -1;
  };

  const checkMicrophoneAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (err) {
      return false;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // ───── Final Render ─────────────
  // If there is no token or token validation failed, show a blank modal with an error toast.
  if (!token || isValidToken === false) {
    return (
      <>
        <Modal open={open} onClose={onClose}>
          <Box sx={{ width: "100vw", height: "100vh", bgcolor: "#ffffff" }} />
        </Modal>
        <AlertToast
          open={true}
          message="Token validation unsuccessful"
          onClose={() => {}}
          type="error"
        />
      </>
    );
  }

  // Otherwise, render the calling UI.
  return (
    <>
      <Modal open={open && connectionState !== "closed"} onClose={handleCallEnd}>
        <Box
          sx={{
            width: 440,
            height: 408,
            bgcolor: "#FFFFFF",
            borderRadius: "16px",
            border: "1px solid #E0F7F8",
            boxShadow: 24,
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <audio ref={audioRef} />
          <div className="flex gap-1 flex-col w-full">
            <div className="flex gap-2 items-center justify-between">
              <Typography variant="h6" fontWeight={600}>
                {tripInfo?.driverName || "NA"}
              </Typography>
              <div className="flex items-center gap-1">
                <Typography variant="caption" color="secondary">
                  Network
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "rgba(39, 174, 96, 0.15)",
                    px: "8px",
                    py: "4px",
                    borderRadius: "8px",
                    display: "inline-block",
                  }}
                >
                  <Typography variant="overline" color="#219653" fontWeight={400}>
                    Strong
                  </Typography>
                </Box>
              </div>
            </div>
            <Typography variant="button" fontWeight={400}>
              {vehicleRegNo || "NA"}
            </Typography>
          </div>

          <img src={avatar} alt="avatar" height={120} width={120} />

          <Typography variant="button">
            {connectionState === "connected"
              ? "Connected"
              : connectionState === "connecting"
              ? "Connecting..."
              : connectionState === "disconnected"
              ? "Disconnected"
              : "Calling..."}
          </Typography>
          <Typography variant="button" className="mt-1">
            {connectionState === "connected" &&
              `Call Duration: ${formatTime(callDuration)}`}
          </Typography>

          <div className="flex gap-6 justify-center">
            <img
              src={!isMuted ? muteIcon : unmuteIcon}
              alt="mute"
              height={48}
              width={48}
              onClick={handleMuteToggle}
              style={{
                cursor: connectionState !== "connected" ? "not-allowed" : "pointer",
                opacity: connectionState !== "connected" ? 0.5 : 1,
              }}
            />
            <img
              src={endCallIcon}
              alt="end call"
              height={48}
              width={48}
              onClick={handleCallEnd}
              style={{ cursor: "pointer" }}
            />
          </div>
        </Box>
      </Modal>

      <AlertToast
        open={toast.open}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        type={toast.type as any}
      />
    </>
  );
};

export default SosModal;
